import { FC, useState, useRef, useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import * as Form from '@radix-ui/react-form';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

import { usePageTitle } from 'hooks/usePageTitle';

import { Button } from 'components/parts/Button';
import { PopInView } from 'components/parts/PopInView';
import { Speech } from 'components/parts/Speech';
import { usePutProfileMutation } from 'store/profile';

import styles from './Profile.module.scss';

import { ages } from './utils/ages';
import { genderGroups } from './utils/gender';
import { occupations } from './utils/occupations';
import { regions } from './utils/regions';
import { visitPurposes } from './utils/visitPurpose';

const formSchema = z.object({
  nickname: z.string().trim().min(1, 'ニックネームを入力してね'),
  gender: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)], {
    required_error: '性別を選択してね',
    invalid_type_error: '性別を選択してね',
  }),
  ageGroup: z.union(
    [
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(6),
      z.literal(7),
      z.literal(8),
    ],
    {
      required_error: '年代を選択してね',
      invalid_type_error: '年代を選択してね',
    }
  ),
  occupation: z.union(
    [
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(6),
      z.literal(7),
      z.literal(8),
      z.literal(9),
    ],
    {
      required_error: '職業を選択してね',
      invalid_type_error: '無効な職業です',
    }
  ),
  residence: z
    .string()
    .nonempty('住居を選択してね')
    .regex(/^(0[1-9]|[1-3][0-9]|4[0-7])$/, {
      message: '住居を選択してね',
    }),
  visitPurpose: z.union(
    [
      z.literal(0),
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
      z.literal(6),
      z.literal(7),
      z.literal(8),
      z.literal(99),
    ],
    {
      required_error: '参加したきっかけを選択してね',
      invalid_type_error: '参加したきっかけを選択してね',
    }
  ),
});

type Props = {
  onSuccessSubmitCallback?: () => void;
};

export const Profile: FC<Props> = ({ onSuccessSubmitCallback }) => {
  usePageTitle('プロフィール');
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors, dirtyFields },
    setError,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nickname: undefined,
      occupation: undefined,
      gender: undefined,
      ageGroup: undefined,
      residence: undefined,
      visitPurpose: undefined,
    },
  });

  // 本文のエリアを参照するためのref
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    const target = contentAreaRef.current;
    if (!target) return;

    setTimeout(() => {
      // フォーカス時に本文の末尾がviewportに入るようにスクロール
      target.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 500);
  }, []);

  const [putProfile] = usePutProfileMutation();

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const result = await putProfile({ ...values }).unwrap();

      // リクエスト成功後の処理
      if (result && onSuccessSubmitCallback) {
        console.log(`Profile: 送信完了`);
        onSuccessSubmitCallback();
      }
    } catch (error) {
      console.log(error);
      setError('root.client', { type: 'clientError', message: 'Profile: エラーが発生しました。' });
    }
  };

  const handleNextStep = async () => {
    // React Hook Formのバリデーションを実行
    const isValid = await trigger('nickname');
    if (!isValid) {
      return; // バリデーションエラーがあれば処理を止める
    }
    setCurrentStep(2);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <div className={styles.body} ref={contentAreaRef}>
          <div
            data-step={1}
            style={{ display: currentStep === 1 ? 'block' : 'none' }}
            className={styles.talk}
          >
            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.balloon}>
              <Speech>
                よーし、頑張ってレッド・カウのメンバーになったんだ。入ったからには、福島県のすごい宝を見つけてみせる！
              </Speech>
            </PopInView>

            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.balloon}>
              <Speech>
                そういえば、レッド・カウからメンバーとして活動するとき、自分のことを教えてくれ……って言われていたっけ。
              </Speech>
            </PopInView>

            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.balloon}>
              <Speech>{`どれどれ、まずはコードネームを決めてくれ、だって。\n冒険の中でみんなに呼ばれたい名前かぁ……どんな名前がいいかな？`}</Speech>
            </PopInView>

            <PopInView rootMargin="-64px 0px -340px 0px">
              <div className={styles.nickname}>
                {/* ニックネーム */}
                <input
                  id="nickname_input"
                  type="text"
                  placeholder="アマナ"
                  {...register('nickname')}
                  data-error={Boolean(errors.nickname)}
                  className={styles.inputText}
                  onFocus={handleScroll}
                />
                {errors.nickname && (
                  <div className={styles.formMessage}>{errors.nickname.message}</div>
                )}

                <Button
                  text="こたえる"
                  buttonType="button"
                  handleClick={handleNextStep}
                  styleType="secondary"
                />
              </div>
            </PopInView>
          </div>
          <div
            data-step={2}
            style={{ display: currentStep === 2 ? 'block' : 'none' }}
            className={styles.talk}
          >
            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.balloon}>
              <Speech>{`よし、${watch('nickname')}に決めた！\n${watch(
                'nickname'
              )}の名前がずっと歴史に残るくらい、頑張るぞ！`}</Speech>
            </PopInView>

            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.balloon}>
              <Speech>{`次に自分のことについて、だって。\n冒険の中で忙しくならないうちに、書いておこう！`}</Speech>
            </PopInView>

            <PopInView rootMargin="-64px 0px -340px 0px" className={styles.form}>
              <Form.Root className={styles.formRoot} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formBody}>
                  <div className={styles.formInner}>
                    <Form.Field className={styles.formField} name="gender">
                      <p className={styles.formLabel}>
                        性別<span className={styles.formLabelRequired}>必須</span>
                      </p>

                      <Controller
                        name="gender"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup.Root
                            className={styles.radioGroup}
                            aria-label="性別"
                            onValueChange={val => onChange(Number(val))}
                            value={String(value)}
                          >
                            {genderGroups.map(({ id, label, value: radioValue }) => (
                              <div
                                key={id}
                                className={styles.radioItemRoot}
                                data-checked={Number(radioValue) === value}
                                data-error={Boolean(errors.gender)}
                              >
                                <RadioGroup.Item
                                  className={styles.radioItem}
                                  value={radioValue}
                                  id={id}
                                >
                                  <RadioGroup.Indicator className={styles.radioGroupIndicator} />
                                </RadioGroup.Item>
                                <label className={styles.radioLabel} htmlFor={id}>
                                  {label}
                                </label>
                              </div>
                            ))}
                          </RadioGroup.Root>
                        )}
                      />
                      {errors.gender && (
                        <Form.Message className={styles.formMessage}>性別を選択してね</Form.Message>
                      )}
                    </Form.Field>
                    <Form.Field className={styles.formField} name="question">
                      <label className={styles.formLabel} htmlFor="ageGroup">
                        年代<span className={styles.formLabelRequired}>必須</span>
                      </label>

                      <div className={styles.selectRoot}>
                        <select
                          id="ageGroup"
                          defaultValue=""
                          className={styles.select}
                          {...register('ageGroup', {
                            valueAsNumber: true,
                          })}
                          data-init={!dirtyFields.ageGroup}
                          data-error={Boolean(errors.ageGroup)}
                        >
                          <option value="" className={styles.hiddenOption} disabled>
                            年代を選択してね
                          </option>
                          {ages.map(age => (
                            <option key={age.id} value={age.value} className={styles.option}>
                              {age.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {errors.ageGroup && (
                        <Form.Message className={styles.formMessage}>年代を選択してね</Form.Message>
                      )}
                    </Form.Field>
                    {/* 職業 */}
                    <Form.Field className={styles.formField} name="occupation">
                      <label className={styles.formLabel} htmlFor="occupation">
                        職業<span className={styles.formLabelRequired}>必須</span>
                      </label>
                      <div className={styles.selectRoot}>
                        <select
                          id="occupation"
                          defaultValue=""
                          className={styles.select}
                          {...register('occupation', {
                            valueAsNumber: true,
                          })}
                          data-init={!dirtyFields.occupation}
                          data-error={Boolean(errors.occupation)}
                        >
                          <option value="" className={styles.hiddenOption} disabled>
                            職業を選択してね
                          </option>
                          {occupations.map(occupation => (
                            <option key={occupation.id} value={occupation.value}>
                              {occupation.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.occupation && (
                        <Form.Message className={styles.formMessage}>職業を選択してね</Form.Message>
                      )}
                    </Form.Field>
                    <Form.Field className={styles.formField} name="question">
                      <label className={styles.formLabel} htmlFor="residence">
                        住居<span className={styles.formLabelRequired}>必須</span>
                      </label>

                      <div className={styles.selectRoot}>
                        <select
                          id="residence"
                          defaultValue=""
                          className={styles.select}
                          {...register('residence')}
                          data-error={Boolean(errors.residence)}
                          data-init={!dirtyFields.residence}
                        >
                          <option value="" className={styles.hiddenOption} disabled>
                            都道府県を選択してね
                          </option>
                          {regions.map(region => (
                            <optgroup key={region.region} label={region.region}>
                              {region.prefectures.map(prefecture => (
                                <option
                                  key={prefecture.code}
                                  value={prefecture.code}
                                  className={styles.option}
                                >
                                  {prefecture.name}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>

                      {errors.residence && (
                        <Form.Message className={styles.formMessage}>
                          都道府県を選択してね
                        </Form.Message>
                      )}
                    </Form.Field>
                    {/* 参加したきっかけ */}
                    <Form.Field className={styles.formField} name="visitPurpose">
                      <label className={styles.formLabel} htmlFor="visitPurpose">
                        参加したきっかけ<span className={styles.formLabelRequired}>必須</span>
                      </label>
                      <div className={styles.selectRoot}>
                        <select
                          id="visitPurpose"
                          defaultValue=""
                          className={styles.select}
                          {...register('visitPurpose', {
                            valueAsNumber: true,
                          })}
                          data-init={!dirtyFields.visitPurpose}
                          data-error={Boolean(errors.visitPurpose)}
                        >
                          <option value="" className={styles.hiddenOption} disabled>
                            参加したきっかけを選択してね
                          </option>
                          {visitPurposes.map(purpose => (
                            <option key={purpose.id} value={purpose.value}>
                              {purpose.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.visitPurpose && (
                        <Form.Message className={styles.formMessage}>
                          参加したきっかけを選択してね
                        </Form.Message>
                      )}
                    </Form.Field>
                  </div>
                </div>
                <div className={styles.formFoot}>
                  <div className={styles.formSubmit}>
                    <Form.Submit asChild>
                      <div className={styles.buttonRoot}>
                        <Button text="こたえる" buttonType="submit" styleType="secondary" />
                      </div>
                    </Form.Submit>
                  </div>
                </div>
              </Form.Root>
            </PopInView>
          </div>
        </div>
      </div>
    </div>
  );
};
